import { remainingCents, type Payable, type PaymentState } from './payments'

/**
 * Narrowing the payments list.
 *
 * spec.md §18 asks for a filter sheet on type, status, supplier, date range
 * and amount. Every one of those is a question someone asks out loud — "wat
 * staat er nog open bij Metro", "wat is er boven de vijfhonderd" — so the
 * filter is those questions, not a query builder.
 *
 * Pure, and separate from the sheet that sets it, because the interesting
 * part is what "matches" means and that deserves tests rather than a click
 * through five menus.
 */

export interface Filters {
  states: PaymentState[]
  supplier: string
  minCents: number | null
  maxCents: number | null
  /** Only what cannot wait. */
  hardOnly: boolean
}

export const NO_FILTERS: Filters = {
  states: [],
  supplier: '',
  minCents: null,
  maxCents: null,
  hardOnly: false,
}

export function isFiltering(f: Filters): boolean {
  return (
    f.states.length > 0 ||
    f.supplier.trim() !== '' ||
    f.minCents !== null ||
    f.maxCents !== null ||
    f.hardOnly
  )
}

export function countActive(f: Filters): number {
  return (
    (f.states.length > 0 ? 1 : 0) +
    (f.supplier.trim() ? 1 : 0) +
    (f.minCents !== null || f.maxCents !== null ? 1 : 0) +
    (f.hardOnly ? 1 : 0)
  )
}

export function applyFilters(items: Payable[], f: Filters): Payable[] {
  const needle = f.supplier.trim().toLowerCase()

  return items.filter((item) => {
    if (f.states.length > 0 && !f.states.includes(item.state)) return false
    if (f.hardOnly && !item.isHard) return false

    if (needle) {
      // Supplier or description: people search for what they call the thing,
      // and half the time that is the description on a manual entry.
      const haystack = `${item.supplierName ?? ''} ${item.description}`.toLowerCase()
      if (!haystack.includes(needle)) return false
    }

    // Compare against what is still owed, not the original amount. "Boven de
    // vijfhonderd" means five hundred still to pay.
    const amount = remainingCents(item)
    if (f.minCents !== null && amount < f.minCents) return false
    if (f.maxCents !== null && amount > f.maxCents) return false

    return true
  })
}

export const STATE_FILTERS: { state: PaymentState; label: string }[] = [
  { state: 'to_pay', label: 'Te betalen' },
  { state: 'planned', label: 'Gepland' },
  { state: 'needs_attention', label: 'Vraagt aandacht' },
  { state: 'paid', label: 'Betaald' },
]
